import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField
} from '@mui/material';
import { useEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import {
    CostGroupDto,
    CreateCostGroupRequest,
    ProductCategoryDto,
    ProductGroupDto
} from '../../../common/PricePrgClient';
import { RootState } from '../../../store';
import { createCostGroup, showEditCostGroupDialog, updateCostGroup } from '../redux/calculationSlices';
import ConditionSelector from './ConditionSelector';

const mapStateToProps = (state: RootState) => ({
    calculation: state.calculation.calculation,
    editingCostGroup: state.calculation.editingCostGroup,
    isEditCostGroupDialogVisible: state.calculation.isEditCostGroupDialogVisible
});

type Props = ReturnType<typeof mapStateToProps>;

const EditCostGroupDialog = ({ calculation, editingCostGroup, isEditCostGroupDialogVisible }: Props) => {
    const dispatch = useDispatch();

    const dispatchHideNewCostGroupDialog = () => {
        dispatch(showEditCostGroupDialog(null));
    };

    const [editFormState, setFormState] = useState({
        name: editingCostGroup?.name ?? '',
        description: editingCostGroup?.description,
        useBasePrice: editingCostGroup?.useBasePrice ?? false,
        costGroupType: editingCostGroup?.costGroupType ?? 0,
        productGroups: editingCostGroup?.appliesToProductGroups ?? [],
        productCategories: editingCostGroup?.appliesToProductCategories ?? [],
        productCategories2: editingCostGroup?.appliesToProductCategories2 ?? [],
        isDisabled: editingCostGroup?.isDisabled ?? false
    });
    // TODO: This shouldn't be needed? useState should work without useEffect
    useEffect(() => {
        if (editingCostGroup != null) {
            setFormState({
                name: editingCostGroup.name,
                description: editingCostGroup.description,
                useBasePrice: editingCostGroup.useBasePrice,
                costGroupType: editingCostGroup.costGroupType ?? 0,
                productGroups: editingCostGroup.appliesToProductGroups ?? [],
                productCategories: editingCostGroup.appliesToProductCategories ?? [],
                productCategories2: editingCostGroup.appliesToProductCategories2 ?? [],
                isDisabled: editingCostGroup.isDisabled ?? false
            });
        }
    }, [editingCostGroup]);

    const handleNameChange = (inputValue: string) => {
        setFormState({
            ...editFormState,
            name: inputValue
        });
    };

    const handleDescriptionChange = (inputValue: string) => {
        setFormState({
            ...editFormState,
            description: inputValue
        });
    };

    const handleUseBasePrice = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormState({
            ...editFormState,
            useBasePrice: event.target.checked
        });
    };
    const handleGroupTypeChange = (event: SelectChangeEvent<number>) => {
        setFormState({
            ...editFormState,
            costGroupType: Number(event.target.value)
        });
    };

    const handleProductGroupChanged = (event: React.ChangeEvent<unknown>, child: ProductGroupDto[]) => {
        setFormState({
            ...editFormState,
            productGroups: child.map(c => c.productGroupId) ?? []
        });
    };

    const handleProductCategoriesChanged = (
        event: React.ChangeEvent<unknown>,
        child: ProductCategoryDto[]
    ) => {
        setFormState({
            ...editFormState,
            productCategories: child.map(c => c.productCategoryId) ?? []
        });
    };

    const handleProductCategories2Changed = (
        event: React.ChangeEvent<unknown>,
        child: ProductCategoryDto[]
    ) => {
        setFormState({
            ...editFormState,
            productCategories2: child.map(c => c.productCategoryId) ?? []
        });
    };

    const handleIsDisabled = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFormState({
            ...editFormState,
            isDisabled: event.target.checked
        });
    };
    const handleSubmit = () => {
        if (editingCostGroup?.inheritedFrom != null) {
            dispatchCreateNewCostGroup();
        } else {
            dispatchUpdateCostGroup();
        }
    };

    const dispatchUpdateCostGroup = () => {
        if (editingCostGroup != null) {
            const updateCostGroupDto: CostGroupDto = {
                calculationId: calculation.calculationId,
                name: editFormState.name,
                description: editFormState.description,
                useBasePrice: editFormState.useBasePrice,
                order: calculation.costGroups != null ? calculation.costGroups?.length + 1 : 0,
                costGroupId: editingCostGroup.costGroupId,
                costGroupType: editFormState.costGroupType,
                appliesToProductGroups: editFormState.productGroups ?? [],
                appliesToProductCategories: editFormState.productCategories ?? [],
                appliesToProductCategories2: editFormState.productCategories2 ?? [],
                isDisabled: editFormState.isDisabled ?? false,
                costs: []
            };
            dispatch(updateCostGroup.request(updateCostGroupDto));
        }
    };

    const dispatchCreateNewCostGroup = () => {
        const costGroupDto: CreateCostGroupRequest = {
            calculationId: calculation.calculationId,
            name: editFormState.name,
            description: editFormState.description,
            useBasePrice: editFormState.useBasePrice,
            order: calculation.costGroups != null ? calculation.costGroups?.length + 1 : 0,
            overridesCostGroupId:
                editingCostGroup?.inheritedFrom != null
                    ? editingCostGroup?.inheritedFrom.inheritedFromPricelistId
                    : undefined,
            costGroupType: editFormState.costGroupType,
            appliesToProductGroupIds: editFormState.productGroups ?? [],
            appliesToProductCategoryIds: editFormState.productCategories ?? [],
            appliesToProductCategory2Ids: editFormState.productCategories2 ?? [],
            isDisabled: editFormState.isDisabled ?? false
        };

        dispatch(
            createCostGroup.request({
                pricelistId: calculation.pricelistId,
                costGroupDto: costGroupDto
            })
        );
    };

    const getDialogTitle = () => {
        if (editingCostGroup?.inheritedFrom != null) {
            return 'Create cost group';
        }
        return 'Edit cost group';
    };

    return (
        <Dialog
            onClose={dispatchHideNewCostGroupDialog}
            aria-labelledby="simple-dialog-title"
            open={isEditCostGroupDialogVisible}
        >
            <DialogTitle id="simple-dialog-title">{getDialogTitle()}</DialogTitle>
            <DialogContent>
                <ConditionSelector
                    costGroupProductCategories={editingCostGroup?.appliesToProductCategories ?? []}
                    costGroupProductCategories2={editingCostGroup?.appliesToProductCategories2 ?? []}
                    costGroupProductGroups={editingCostGroup?.appliesToProductGroups ?? []}
                    handleProductCategoryChanged={handleProductCategoriesChanged}
                    handleProductCategory2Changed={handleProductCategories2Changed}
                    handleProductGroupChanged={handleProductGroupChanged}
                />
                <TextField
                    autoFocus
                    margin="dense"
                    id="name"
                    label="Name"
                    type="text"
                    fullWidth={true}
                    onChange={e => handleNameChange(e.target.value)}
                    value={editFormState.name}
                />
                <TextField
                    margin="dense"
                    id="description"
                    label="Description"
                    type="text"
                    fullWidth={true}
                    onChange={e => handleDescriptionChange(e.target.value)}
                    value={editFormState.description}
                />
                <Select onChange={handleGroupTypeChange} defaultValue={0} value={editFormState.costGroupType}>
                    <MenuItem value="0">Stand alone calculation</MenuItem>
                    <MenuItem value="1">Grouped percentages</MenuItem>
                </Select>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={editFormState.useBasePrice}
                            onChange={e => handleUseBasePrice(e)}
                            name="useBasePrice"
                            color="primary"
                        />
                    }
                    label="Calculate using base price"
                />
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={editFormState.isDisabled}
                            onChange={e => handleIsDisabled(e)}
                            name="isDisabled"
                            color="primary"
                        />
                    }
                    label="Disabled"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={dispatchHideNewCostGroupDialog} color="primary">
                    Close
                </Button>
                <Button onClick={() => handleSubmit()} color="primary">
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default connect(mapStateToProps)(EditCostGroupDialog);
